import React, { useEffect, useState, useRef } from "react";
import { useAuthCheck } from "../components/Auth";
import { useParams, useNavigate } from "react-router-dom";
import { api, API_ROUTES } from "../components/Api";
import Loading from "../components/Loading";

function formatDate(dateStr) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("fr-FR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });

}

function formatTime(dateStr) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("fr-FR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
}

function ViewProduct() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { checkToken } = useAuthCheck();

    useEffect(() => { checkToken(); }, [checkToken]);

    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            try {
                const res = await api.get(`${API_ROUTES.PRODUCTS}/${id}`);
                const productData = res.data;

                // Vérifie si le produit a au moins une carte avec un license_card_id
                if (productData.cards?.[0]?.license_card_id) {
                    // Récupère toutes les licences
                    const l = await api.get(`${API_ROUTES.LICENSECARDS}`);
                    // Trouve la licence correspondante
                    const foundLicense = l.data.find(
                        lic => lic.id === productData.cards[0].license_card_id
                    );

                    if (foundLicense) {
                        // Remplace l'ID par le nom
                        productData.cards[0].license_card_id = foundLicense.license_card;
                    }
                }

                setProduct(productData);
            } catch (err) {
                console.error("Erreur lors de la récupération du produit :", err);
                setError("Impossible de récupérer le produit.");
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [id]);

    const asSold = async () => {
        const confirmed = window.confirm(
            "Êtes-vous sûr de vouloir marquer ce produit comme vendu ? \n CETTE ACTION EST IRRÉVERSIBLE."
        );
        if (!confirmed) return;

        try {
            const res = await api.patch(`${API_ROUTES.PRODUCTS}/${product.id}/sold`);
            setProduct(res.data); // Met à jour le produit avec sold = 1
            window.location.reload();
        } catch (err) {
            console.error("Erreur lors de la mise à jour du statut vendu :", err);
            alert("Impossible de mettre le produit comme vendu.");
        }
    };

    const relatedTables = [
        { name: "cards", label: "Carte", champs: [{ key: "license_card_id", label: "License" }] },
        { name: "books", label: "Livre", champs: [{ key: "book_page", label: "Page" }, { key: "book_color", label: "Couleur" }] },
        { name: "posters", label: "Poster", champs: [{ key: "poster_serie", label: "Série" }, { key: "poster_character", label: "Personnage" }] },
        { name: "multimedia", label: "Multimédia", champs: [{ key: "multimedia_code", label: "Code" }, { key: "multimedia_artist", label: "Artiste" }] },
        { name: "goodies", label: "Goodie", champs: [{ key: "goodie_serie", label: "Série" }, { key: "goodie_character", label: "Personnage" }] },
        { name: "figures", label: "Figure", champs: [{ key: "figure_serie", label: "Série" }, { key: "figure_character", label: "Personnage" }] },
    ];

    const fileInputRef = useRef(null);

    const openFilePicker = () => fileInputRef.current?.click();

    const handleUploadImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("picture", file);

        try {
            // endpoint défini ci-dessous côté Laravel
            const res = await api.post(`${API_ROUTES.PRODUCTS}/${id}/photo`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setProduct(res.data); // on récupère le produit à jour (avec picture)
        } catch (err) {
            console.error("Upload image error:", err);
            alert("Échec de l’upload de l’image.");
        } finally {
            e.target.value = ""; // reset le champ file
            window.location.reload();
        }
    };

    if (loading) return <Loading message="Chargement du produit..." />;
    if (error) return <p className="text-red-500">{error}</p>;
    if (!product) return <p>Aucun produit trouvé.</p>;

    return (
        <div className="container mx-auto p-4 space-y-4">
            <h1 className="text-3xl font-bold dark:text-white">{product.name}</h1>


            <div className="grid grid-cols-2 gap-4">
                {/* Informations générales */}
                <div className="space-y-2">
                    <p><strong>Référence :</strong> {product.reference}</p>
                    <p><strong>N° Lot :</strong> {product.batch}</p>
                    <p><strong>Type :</strong> {product.type?.type_name}</p>
                    <p><strong>Sous-type :</strong> {product.subtype?.subtype_name}</p>
                    {relatedTables.map(table => {
                        const items = product[table.name]; // product.cards, product.books, etc.
                        if (!items || items.length === 0) return null;

                        return (
                            <div key={table.name}>
                                {items.map(item => (
                                    <div key={item.id} className="ml-4 space-y-1">
                                        {table.champs.map(champ => {
                                            const value = item[champ.key];
                                            if (value === null || value === "") return null;
                                            return <p key={champ.key}><strong>{champ.label} :</strong> {value}</p>;
                                        })}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                    <p><strong>Langue :</strong> {product.language?.language_name}</p>
                    <p><strong>Packaging :</strong> {product.packaging?.packaging_name}</p>
                    <p><strong>Poids :</strong> {product.weight} g</p>
                    <p><strong>Prix d'achat :</strong> {product.buy} €</p>
                    <p><strong>Prix de vente :</strong> {product.sell} €</p>
                    <p><strong>Publication :</strong> {formatDate(product.publication)}</p>
                    <p><strong>Marque :</strong> {product.brand}</p>
                    <p><strong>EAN :</strong> {product.ean}</p>
                    <p><strong>Dimension :</strong> {product.dimension}</p>
                    <p><strong>Commentaire :</strong> {product.commentary}</p>
                    <p><strong>Date d'ajout :</strong> {formatTime(product.created_at)}</p>
                    {product.sold === 1 && (<p><strong>Archivé en vente le :</strong> {formatTime(product.updated_at)}</p>)}

                </div>

                {/* Photo à droite */}
                <div className="ml-auto">
                    {product.sold === 0 && (<>
                        {product.picture && (
                            <img src={`${API_ROUTES.IMG}${product.picture}`} alt={product.name} className="max-h-[500px] border rounded" />
                        )}
                        {/* Ajouter/Modifier image */}
                        {!product.picture ? (
                            <button
                                onClick={openFilePicker}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 w-full"
                            >
                                Ajouter une image
                            </button>
                        ) : (
                            <button
                                onClick={openFilePicker}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 w-full"
                            >
                                Modifier l’image
                            </button>
                        )}
                    </>)}
                </div>


                {/* Liens associés */}
                {product.links && product.links.length > 0 && (
                    <div className="space-y-2 mt-4 bg-gray-100 text-center p-2 rounded">
                        <ul className="list-disc pl-5">
                            {product.links.map(link => (
                                <li key={link.id} className="list-none">
                                    <a
                                        href={link.link_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 underline"
                                    >
                                        {link.type_link?.type_link_name || "Lien"}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Boutons d’action */}
            {product.sold === 0 && (
                <div className="flex justify-between gap-2 mb-4">
                    {/* Modifier produit */}
                    <button
                        onClick={() => navigate(`/edit-product/${product.id}`)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Modifier le produit
                    </button>

                    <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={handleUploadImage}
                        className="hidden"
                    />

                    {/* Produit vendu (si pas déjà vendu) */}

                    <button
                        onClick={asSold}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                        Produit vendu
                    </button>
                </div>
            )}
        </div>
    );
}

export default ViewProduct;
